import React, { ReactNode, useMemo } from 'react';

import PropTypes from 'prop-types';

import AnimateHeight from '@adsk/alloy-react-animate-height';
import {
  useTheme,
  useComponentIcons,
  type CombinedTheme,
} from '@adsk/alloy-react-styles';
import {
  stylePropType,
  StylableComponent,
  StyleProp,
} from '@adsk/alloy-react-theme';

const getChevronAnimation = ({
  expanded,
  theme,
}: {
  expanded: boolean;
  theme: CombinedTheme;
}) => {
  const iconTokens = theme.tokens?.component?.accordion?.icon;

  return {
    transform: expanded
      ? iconTokens?.['transform-expanded']
      : iconTokens?.['transform-collapsed'],
    transitionProperty: iconTokens?.['transition-property'],
    transitionDuration: iconTokens?.['transition-duration'],
    transitionTimingFunction: iconTokens?.['transition-timing-function'],
  };
};

export type AccordionItemProps = Omit<
  StylableComponent<HTMLDivElement>,
  'title'
> & {
  expanded?: boolean;
  label?: ReactNode;
  onExpand?: (event: React.MouseEvent<HTMLElement>) => void;
  rightAligned?: boolean;
  title?: ReactNode | string;
  animationTimeout?: number;
  onAnimationComplete?: () => void;
  headerClassName?: string;
  headerStyle?: StyleProp;
  buttonProps?: {
    id?: string;
    'aria-controls'?: string;
  };
  containerProps?: Omit<
    React.ComponentPropsWithoutRef<typeof AnimateHeight>,
    'isOpen' | 'timeout' | 'onAnimationComplete' | 'children'
  >;
};

/**
 * An expandable panel component which can be used in an accordion.
 */
const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  (
    {
      style,
      className,
      headerStyle,
      headerClassName,
      children,
      expanded = false,
      label,
      onExpand,
      rightAligned = false,
      title,
      animationTimeout,
      buttonProps,
      containerProps,
      onAnimationComplete,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const { expandIcon: ExpandIcon } = useComponentIcons('accordion');

    const chevronAnimation = useMemo(
      () => getChevronAnimation({ expanded, theme }),
      [expanded, theme],
    );

    return (
      <div
        className={className}
        css={[
          {
            marginTop: theme.tokens?.component?.accordion?.['margin-top'],
            marginBottom: theme.tokens?.generic.spacing.none,
            width: '100%',
            color: theme.tokens?.component?.accordion?.['text-color'],
            ':focus': {
              boxShadow: [
                theme.tokens?.component?.accordion?.['box-shadow-inset']?.focus,
                theme.tokens?.component?.accordion?.['box-shadow-x']?.focus,
                theme.tokens?.component?.accordion?.['box-shadow-y']?.focus,
                theme.tokens?.component?.accordion?.['box-shadow-blur']?.focus,
                theme.tokens?.component?.accordion?.['box-shadow-spread']
                  ?.focus,
                theme.tokens?.component?.accordion?.['box-shadow-color']?.focus,
              ]
                .filter(Boolean)
                .join(' '),
            },
          },
          style,
        ]}
        ref={ref}
        role="heading"
        aria-level={1}
        {...props}
      >
        <button
          className={headerClassName}
          aria-expanded={expanded}
          css={[
            {
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              backgroundColor:
                theme.tokens?.component?.accordion?.['background-color'],
              color: theme.tokens?.component?.accordion?.['text-color'],
              cursor: 'pointer',
              textAlign: 'left',
              outline: 'none',
              border: 'none',
              fontFamily: theme.tokens?.component?.accordion?.['font-family'],
              fontWeight: theme.tokens?.component?.accordion?.['font-weight'],
              fontSize: theme.tokens?.component?.accordion?.['font-size'],
              lineHeight: theme.tokens?.component?.accordion?.['line-height'],
              minHeight: theme.tokens?.component?.accordion?.height,
              position: 'relative',
              ...(rightAligned && {
                flexDirection: 'row-reverse',
              }),
              paddingBottom:
                theme.tokens?.component?.accordion?.['padding-bottom'],
              paddingLeft: theme.tokens?.component?.accordion?.['padding-left'],
              paddingRight:
                theme.tokens?.component?.accordion?.['padding-right'],
              paddingTop: theme.tokens?.component?.accordion?.['padding-top'],
            },
            headerStyle,
          ]}
          onClick={onExpand}
          {...buttonProps}
        >
          <span
            css={{
              display: 'inline-flex',
              marginLeft:
                theme.tokens?.component?.accordion?.icon?.['margin-left'],
              marginRight: rightAligned ? theme.tokens?.generic.spacing[16] : 0,
              color: theme.tokens?.component?.accordion?.icon?.fill?.default,
              ':hover': {
                color: theme.tokens?.component?.accordion?.icon?.fill?.hover,
              },
              ...chevronAnimation,
            }}
          >
            <ExpandIcon size={theme.tokens?.component?.accordion?.icon?.size} />
          </span>
          <div
            dir="auto"
            css={[
              {
                flex: 'auto',
                marginBottom:
                  theme.tokens?.component?.accordion?.label?.['margin-bottom'],
                marginLeft: rightAligned
                  ? theme.tokens?.generic.spacing[24]
                  : theme.tokens?.component?.accordion?.label?.['margin-left'],
                marginRight: theme.tokens?.generic.spacing[16],
                marginTop:
                  theme.tokens?.component?.accordion?.label?.['margin-top'],
              },
            ]}
          >
            {title}
          </div>
          {!rightAligned && (
            <div
              dir="auto"
              css={[
                {
                  color:
                    theme.tokens?.component?.accordion?.secondary?.[
                      'text-color'
                    ] ?? theme.tokens?.component?.accordion?.['text-color'],
                  flex: '0 0 auto',
                  marginRight: theme.tokens?.generic.spacing[16],
                },
              ]}
            >
              {label}
            </div>
          )}
        </button>
        <AnimateHeight
          isOpen={!!expanded}
          timeout={animationTimeout}
          onAnimationComplete={onAnimationComplete}
          {...containerProps}
        >
          {children}
        </AnimateHeight>
      </div>
    );
  },
);

AccordionItem.displayName = 'AccordionItem';

AccordionItem.propTypes = {
  /** Styles applied to the root(Content) element */
  style: stylePropType,
  /** Class applied to the root(Content) element */
  className: PropTypes.string,
  /** Styles applied to the button(Header) element */
  headerStyle: stylePropType,
  /** Class applied to the button(Header) element */
  headerClassName: PropTypes.string,
  /** Items to be displayed when the panel is expanded */
  children: PropTypes.any,
  /** Whether or not the panel is expanded */
  expanded: PropTypes.bool,
  /** The secondary label to display on the right of the title bar */
  label: PropTypes.any,
  /** Callback method for when the panel is expanded or contracted */
  onExpand: PropTypes.func,
  /** Whether or not the chevron icon is right-aligned. If true, label will be
   *  hidden */
  rightAligned: PropTypes.bool,
  /** The main title to display on the left of the title bar */
  title: PropTypes.any,
  /** Height animation timeout */
  animationTimeout: PropTypes.number,
  /** Callback when animation is complete */
  onAnimationComplete: PropTypes.func,
  /** Additional props forwarded to the AnimateHeight container (e.g. `style`, `className`, `onEntered`, `onExited`, `height`). `isOpen`, `timeout`, `onAnimationComplete`, and `children` are controlled by AccordionItem and cannot be overridden. */
  containerProps: PropTypes.object,
};

export default AccordionItem;
